/**
 * kidbea-optimized.js  —  Kidbea Shopify Theme
 * -----------------------------------------------
 * Single consolidated JS file covering all theme features.
 *
 * What this file does:
 *  1.  SECURITY FIX  – Replaces eval() in Shopify.onError with JSON.parse()
 *  2.  PERF FIX      – Throttled scroll events (passive listeners, rAF)
 *  3.  PERF FIX      – Debounced resize handlers
 *  4.  PERF FIX      – Cached DOM selectors (no repeated queries)
 *  5.  BUG FIX       – Qty button var scoping (was broken in strict mode)
 *  6.  BUG FIX       – Idempotent qty-button injection (no duplicate -/+ buttons)
 *  7.  BUG FIX       – Event delegation for dynamically added elements
 *  8.  BUG FIX       – min-qty is 1, not 0
 *  9.  PERF          – Single DOMContentLoaded entry point (not many $(document).ready)
 * 10.  PERF          – intersectionObserver polyfill for lazy images
 * 11.  PERF          – Background images only applied once (guarded)
 * 12.  PERF          – Newsletter popup uses proper SameSite cookie
 * 13.  UX            – Smooth scroll-to-top
 * 14.  UX            – Menu closes on outside click
 * 15.  TRACKING      – Google Ads conversion tracking (DOMContentLoaded, passive)
 * 16.  TRACKING      – FB/IG referrer preconnect (only when needed)
 * 17.  PERF          – requestIdleCallback with fallback for idle-loaded class
 *
 * Where to add this file: See instructions at the bottom of this file.
 */

(function ($) {
  'use strict';

  /* ============================================================
   * SECTION 1 — UTILITIES
   * ============================================================ */

  /**
   * throttle(fn, wait)
   * Limits fn to run at most once every `wait` milliseconds.
   * Use for scroll / mousemove handlers.
   */
  function throttle(fn, wait) {
    var lastTime = 0;
    return function () {
      var now = Date.now();
      if (now - lastTime >= wait) {
        lastTime = now;
        fn.apply(this, arguments);
      }
    };
  }

  /**
   * debounce(fn, wait)
   * Delays fn until `wait` ms after the last call.
   * Use for resize handlers, search inputs.
   */
  function debounce(fn, wait) {
    var timer;
    return function () {
      var ctx  = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /**
   * safeInit(name, fn)
   * Wraps feature init in try/catch so one broken feature
   * cannot block others from running.
   */
  function safeInit(name, fn) {
    try {
      fn();
    } catch (e) {
      // Visible only when ?debug=1 is in the URL, silent in production
      if (window.location && window.location.search.indexOf('debug=1') !== -1) {
        console.warn('[Kidbea:' + name + ']', e);
      }
    }
  }

  /**
   * once(el, event, fn, opts)
   * Adds a one-time event listener (polyfills {once: true} for older browsers).
   */
  function once(el, event, fn, opts) {
    var wrapped = function (e) {
      fn(e);
      el.removeEventListener(event, wrapped, opts);
    };
    el.addEventListener(event, wrapped, opts);
  }

  /* ============================================================
   * SECTION 2 — SECURITY FIX: Replace eval() in Shopify.onError
   *
   * Original cart.api.js line 11:
   *   var data = eval('(' + XMLHttpRequest.responseText + ')');
   *
   * eval() is:
   *   - A security vulnerability (can execute injected code)
   *   - Slower than JSON.parse()
   *   - Flagged by CSP (Content Security Policy)
   *
   * We override Shopify.onError here so this file's version runs
   * instead, without touching cart.api.js.
   * ============================================================ */

  if (window.Shopify) {
    Shopify.onError = function (xhr) {
      var data = {};
      try {
        data = JSON.parse(xhr.responseText); // Safe replacement for eval()
      } catch (parseErr) {
        // Response was not valid JSON — use empty object, show generic message
      }

      var str;
      if (data.message) {
        str = data.description || data.message;
      } else if (typeof Shopify.fullMessagesFromErrors === 'function') {
        str = 'Error : ' + Shopify.fullMessagesFromErrors(data).join('; ') + '.';
      } else {
        str = 'An error occurred. Please try again.';
      }

      jQuery('.error_message').text(str);
      jQuery('#modalAddToCartError').modal('toggle');

      setTimeout(function () {
        jQuery('.shopping__cart').removeClass('shopping__cart__on');
        jQuery('.body__overlay').removeClass('is-visible');
      }, 2000);
    };
  }

  /* ============================================================
   * SECTION 3 — STICKY CART
   *
   * Original in theme.js was an unthrottled $(window).scroll()
   * handler — fires on every pixel of scroll, causing layout
   * thrashing. Replaced with:
   *   - throttle() to limit calls to 100ms
   *   - passive: true so browser does not wait for preventDefault
   *   - requestAnimationFrame for DOM writes
   * ============================================================ */

  function initStickyCart() {
    var $stickyCart = $('.sticky_cart');
    if (!$stickyCart.length) return;

    var winHeight  = window.innerHeight;
    var ticking    = false;

    // Update on resize (debounced — no need to recalculate every px)
    window.addEventListener('resize', debounce(function () {
      winHeight = window.innerHeight;
    }, 250), { passive: true });

    var onScroll = throttle(function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var scrolled = window.pageYOffset || document.documentElement.scrollTop;
        if (scrolled > winHeight) {
          $stickyCart[0].classList.add('show');
        } else {
          $stickyCart[0].classList.remove('show');
        }
        ticking = false;
      });
    }, 100);

    window.addEventListener('scroll', onScroll, { passive: true });

    // Sticky-cart add-to-cart proxy button
    var $stickyBtn = $('#btn-sticky-cart');
    if ($stickyBtn.length) {
      $stickyBtn.on('click', function () {
        var realBtn = document.querySelector('.paymentButtonsWrapper #AddToCart');
        if (realBtn) realBtn.click();
      });
    }
  }

  /* ============================================================
   * SECTION 4 — QTY BUTTONS (cart-plus-minus)
   *
   * Bugs fixed from original footer-js.liquid:
   *   1. `var newVal` declared inside both if/else branches — in
   *      strict mode the inner else `newVal = 1` would be
   *      ReferenceError. Fixed with single declaration.
   *   2. Minimum qty was 0 — should be 1 (can't order 0 items).
   *   3. Buttons were injected unconditionally — caused duplicate
   *      -/+ buttons on Shopify section reload. Fixed with guard.
   *   4. Direct binding on `.qtybutton` — breaks for dynamically
   *      added elements. Fixed with event delegation.
   * ============================================================ */

  function initQtyButtons() {
    // Inject -/+ buttons only if not already present (idempotent)
    $('.cart-plus-minus').each(function () {
      var $wrap = $(this);
      if (!$wrap.find('.dec').length) {
        $wrap.prepend('<div class="dec qtybutton" role="button" aria-label="Decrease quantity">-</div>');
      }
      if (!$wrap.find('.inc').length) {
        $wrap.append('<div class="inc qtybutton" role="button" aria-label="Increase quantity">+</div>');
      }
    });

    // Event delegation — works for dynamically rendered cart items
    $(document).on('click', '.qtybutton', function () {
      var $btn    = $(this);
      var $input  = $btn.parent().find('input[type="number"], input[type="text"]').first();
      var oldVal  = parseFloat($input.val()) || 1;
      var newVal;                              // Single declaration — no scoping bug

      if ($btn.hasClass('inc')) {
        newVal = oldVal + 1;
      } else {
        newVal = oldVal > 1 ? oldVal - 1 : 1; // Minimum 1, never 0
      }

      $input.val(newVal).trigger('change');    // Trigger change for any listeners
    });
  }

  /* ============================================================
   * SECTION 5 — AJAX SPIN CART
   *
   * Original used direct binding on `.ajax-spin-cart`.
   * Changed to event delegation so it works with dynamically
   * rendered cart buttons.
   * ============================================================ */

  function initSpinCart() {
    $(document).on('click', '.ajax-spin-cart', function () {
      var $btn = $(this);
      if ($btn.hasClass('loading')) return; // Prevent double-click during load
      $btn.addClass('loading add-item');
      setTimeout(function () { $btn.removeClass('loading'); },  1000);
      setTimeout(function () { $btn.removeClass('add-item'); }, 2000);
    });
  }

  /* ============================================================
   * SECTION 6 — COUNTDOWN TIMERS
   *
   * Guards against missing jQuery countdown plugin so the page
   * doesn't throw if the plugin failed to load.
   * ============================================================ */

  function initCountdown() {
    var $timers = $('[data-countdown]');
    if (!$timers.length) return;
    if (typeof $.fn.countdown !== 'function') return; // Plugin not loaded yet

    $timers.each(function () {
      var $el       = $(this);
      var finalDate = $el.data('countdown');
      if (!finalDate) return;

      $el.countdown(finalDate, function (event) {
        $el.html(event.strftime(
          '<div class="countdown-area">' +
            '<div class="single-countdown">' +
              '<div class="count-number">%D</div>' +
              '<div class="count-title">Days</div>' +
            '</div>' +
            '<div class="single-countdown">' +
              '<div class="count-number">%H</div>' +
              '<div class="count-title">Hours</div>' +
            '</div>' +
            '<div class="single-countdown">' +
              '<div class="count-number">%M</div>' +
              '<div class="count-title">Mins</div>' +
            '</div>' +
            '<div class="single-countdown">' +
              '<div class="count-number">%S</div>' +
              '<div class="count-title">Secs</div>' +
            '</div>' +
          '</div>'
        ));
      });
    });
  }

  /* ============================================================
   * SECTION 7 — RECENTLY VIEWED PRODUCTS
   *
   * Only runs on product pages (matches the original Liquid guard:
   * {% if template contains 'product' %}).
   * ============================================================ */

  function initRecentlyViewed() {
    var isProductPage = document.body.classList.contains('template-product');
    if (!isProductPage) return;
    if (window.Shopify && window.Shopify.Products &&
        typeof Shopify.Products.recordRecentlyViewed === 'function') {
      Shopify.Products.recordRecentlyViewed();
    }
  }

  /* ============================================================
   * SECTION 8 — GOOGLE ADS CONVERSION TRACKING
   *
   * Original in theme.liquid was inside DOMContentLoaded using
   * delegated clicks. Kept the same pattern but moved here so the
   * inline <script defer> block in theme.liquid can be removed.
   * ============================================================ */

  function initConversionTracking() {
    document.addEventListener('click', function (e) {
      if (!window.gtag) return;
      if (e.target.closest && e.target.closest('.cart_button')) {
        gtag('event', 'conversion', {
          send_to: 'AW-11028849460/VpJ_CMmPrZ0YELTG-4op'
        });
      }
      if (e.target.closest && e.target.closest('.shopify-payment-button__button')) {
        gtag('event', 'conversion', {
          send_to: 'AW-11028849460/AQpYCMyPrZ0YELTG-4op'
        });
      }
    }, { capture: true, passive: true });
  }

  /* ============================================================
   * SECTION 9 — FACEBOOK / INSTAGRAM REFERRER PRECONNECT
   *
   * Only injects the preconnect link if the user came from
   * Facebook or Instagram — no unnecessary DNS resolution for
   * everyone else.
   * ============================================================ */

  function initFBPreconnect() {
    var ref = document.referrer || '';
    if (ref.indexOf('facebook.com') === -1 && ref.indexOf('instagram.com') === -1) return;
    if (document.querySelector('link[href="https://connect.facebook.net"]')) return; // Already added

    var link         = document.createElement('link');
    link.rel         = 'preconnect';
    link.href        = 'https://connect.facebook.net';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  /* ============================================================
   * SECTION 10 — IDLE CALLBACK
   *
   * Marks the document as idle-loaded for optional deferred work.
   * Falls back to setTimeout(500) for browsers without rIC.
   * ============================================================ */

  function initIdleCallback() {
    once(window, 'load', function () {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(function () {
          document.documentElement.classList.add('idle-loaded');
        }, { timeout: 2000 });
      } else {
        setTimeout(function () {
          document.documentElement.classList.add('idle-loaded');
        }, 500);
      }
    }, { passive: true });
  }

  /* ============================================================
   * SECTION 11 — BACKGROUND IMAGES FROM DATA ATTRIBUTE
   *
   * Original: dataBackgroundImage() was defined and called inside
   * every slider init function (theme.js) — causing it to run
   * 4-6 times per page load. This version is idempotent:
   * it marks processed elements so it's safe to call multiple times.
   * ============================================================ */

  function initBgImages() {
    $('[data-bgimg]:not([data-bgimg-done])').each(function () {
      var $el = $(this);
      var url = $el.data('bgimg');
      if (url) {
        $el.css('background-image', 'url(' + url + ')');
        $el.attr('data-bgimg-done', '1');
      }
    });
  }

  /* ============================================================
   * SECTION 12 — PRELOADER FADE-OUT
   *
   * Fades out the preloader on window load, then removes it from
   * the DOM entirely so it doesn't block pointer events.
   * ============================================================ */

  function initPreloader() {
    var $preloader = $('#preloader, .preloader-wrapper, .flone-preloader-wrapper');
    if (!$preloader.length) return;

    once(window, 'load', function () {
      $preloader.fadeOut(400, function () {
        $(this).remove();
      });
    }, { passive: true });
  }

  /* ============================================================
   * SECTION 13 — SCROLL TO TOP
   *
   * Throttled scroll listener. Uses native smooth scroll
   * (window.scrollTo with behavior:'smooth') — no jQuery animation
   * needed, which frees the main thread.
   * ============================================================ */

  function initScrollTop() {
    var $btn = $('#scrollUp, .scroll-to-top');
    if (!$btn.length) return;

    window.addEventListener('scroll', throttle(function () {
      var scrolled = window.pageYOffset || document.documentElement.scrollTop;
      $btn.toggleClass('show', scrolled > 300);
    }, 150), { passive: true });

    $btn.on('click', function (e) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ============================================================
   * SECTION 14 — MENU CLOSE ON OUTSIDE CLICK
   *
   * Closes the clickable main-menu when user clicks anywhere
   * outside of it. Uses a single delegated listener.
   * ============================================================ */

  function initMenuAccessibility() {
    $(document).on('click', function (e) {
      var $target = $(e.target);
      if (!$target.closest('.clickable-mainmenu, .clickable-mainmenu-active').length) {
        $('.clickable-mainmenu').removeClass('inside');
      }
    });
  }

  /* ============================================================
   * SECTION 15 — NATIVE LAZY LOADING / intersectionObserver
   *
   * Modern browsers support loading="lazy" natively.
   * For older browsers we use intersectionObserver as a fallback.
   * Does nothing if native support is available (fast path).
   * ============================================================ */

  function initLazyImages() {
    if ('loading' in HTMLImageElement.prototype) return; // Native — nothing to do

    if (!('intersectionObserver' in window)) return; // Very old browser — skip

    var observer = new intersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isintersecting) return;
        var img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute('data-srcset');
        }
        observer.unobserve(img);
      });
    }, { rootMargin: '200px 0px' }); // Start loading 200px before it enters view

    document.querySelectorAll('img[data-src], img[data-srcset]').forEach(function (img) {
      observer.observe(img);
    });
  }

  /* ============================================================
   * SECTION 16 — COOKIE HELPERS (improved)
   *
   * Original setCookie used document.cookie directly without
   * SameSite — modern browsers require SameSite for security.
   * ============================================================ */

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      expires = '; expires=' + d.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  /* ============================================================
   * SECTION 17 — NEWSLETTER POPUP (cookie-aware)
   *
   * Reads the popup's own data-delay attribute, falls back to
   * 3 000 ms. Re-shows after 7 days (respects user intent).
   * ============================================================ */

  function initNewsletterPopup() {
    var $popup = $('.newsletter-popup-wrapper, #newsletter-popup');
    if (!$popup.length) return;
    if (getCookie('kb_newsletter')) return; // Already shown

    var delay = parseInt($popup.data('delay'), 10);
    if (!delay || isNaN(delay)) delay = 3000;

    setTimeout(function () {
      $popup.addClass('active');
      setCookie('kb_newsletter', '1', 7);
    }, delay);

    // Close triggers
    $popup.on('click', '.newsletter-close, .popup-close, [data-popup-close]', function () {
      $popup.removeClass('active');
    });

    // Close on ESC
    $(document).on('keydown.newsletter', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        $popup.removeClass('active');
        $(document).off('keydown.newsletter');
      }
    });
  }

  /* ============================================================
   * SECTION 18 — SHOPIFY SECTION RELOAD SUPPORT
   *
   * When the Shopify Theme Editor reloads a section, re-run all
   * inits so section-specific features still work in the editor.
   * ============================================================ */

  document.addEventListener('shopify:section:load', function () {
    safeInit('bg-images-reload',  initBgImages);
    safeInit('qty-buttons-reload', initQtyButtons);
    safeInit('countdown-reload',  initCountdown);
  });

  /* ============================================================
   * SECTION 19 — ELEMENT-NOT-FOUND GUARD FOR MODAL CART ERROR
   *
   * Some pages don't have #modalAddToCartError (e.g. cart page).
   * Bootstrap will throw if we try to toggle a non-existent modal.
   * This guard makes it safe.
   * ============================================================ */

  if (window.Shopify && window.jQuery) {
    var _originalModal = jQuery.fn.modal;
    if (_originalModal) {
      var _safePatch = function (method) {
        var orig = _originalModal;
        jQuery.fn.modal = function () {
          if (!this.length) return this; // No element — skip silently
          return orig.apply(this, arguments);
        };
      };
      _safePatch();
    }
  }

  /* ============================================================
   * SECTION 20 — PRODUCT CARD MODULE
   *
   * Moved here from snippets/product-grid-item.liquid where the
   * <style> and <script> blocks were repeating on EVERY product
   * card rendered on the page (20 products = 20 identical blocks).
   *
   * Now injected ONCE into the page by this file.
   * ============================================================ */

  /**
   * Injects product card CSS into <head> exactly once.
   * Uses a guard ID so it's safe to call multiple times.
   */
  function initProductCardStyles() {
    if (document.getElementById('kb-product-card-styles')) return;
    var style = document.createElement('style');
    style.id   = 'kb-product-card-styles';
    style.textContent = [
      '.modal-overlay{display:none;position:fixed;z-index:9999;left:0;top:0;width:100%;height:100%;overflow:auto;background-color:rgba(0,0,0,.5)}',
      '.modal-content{background:#fff;margin:15% auto;padding:20px 25px;border-radius:8px;width:90%;max-width:360px;text-align:center;font-size:16px;box-shadow:0 2px 10px rgba(0,0,0,.2);position:relative}',
      '.modal-close{position:absolute;right:15px;top:8px;font-size:22px;font-weight:700;color:#666;cursor:pointer}',
      '.modal-close:hover{color:#000}',
      '.variant-dropdown{width:100%;padding:8px 12px;font-size:14px;border:1px solid #ccc;border-radius:6px}',
      '.addtocart{display:block;text-align:center;border:1px solid #585858;border-radius:7px;padding:9px;cursor:pointer;background:none;width:100%}',
      '.addtocart:disabled{opacity:.6;cursor:not-allowed}',
      'body.modal-open{overflow:hidden}',
      '.grid-added-notification{position:fixed;top:20px;left:50%;transform:translateX(-50%) translateY(-100px);background:#006AF4;color:#fff;padding:16px 28px;border-radius:8px;font-size:16px;font-weight:600;z-index:999999;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 20px rgba(82,183,136,.4);transition:transform .3s ease,opacity .3s ease;opacity:0}'
    ].join('');
    document.head.appendChild(style);
  }

  /**
   * closeSizeModal — exposed globally because the HTML uses
   * onclick="closeSizeModal()" (inline handler in liquid file).
   */
  window.closeSizeModal = function () {
    var modal = document.getElementById('sizeModal');
    if (modal) modal.style.display = 'none';
    document.body.classList.remove('modal-open');
  };

  /**
   * onVariantSelect — exposed globally because the HTML uses
   * onchange="onVariantSelect(this)" (inline handler in liquid file).
   */
  window.onVariantSelect = function (select) {
    var variantId = select.value;
    var container = select.closest('.product-wrap');
    if (!container) return;
    var button = container.querySelector('.addtocart');
    if (!button) return;

    if (variantId) {
      button.disabled = false;
      button.setAttribute('data-variant-id', variantId);
    } else {
      button.disabled = true;
      button.removeAttribute('data-variant-id');
    }
  };

  /**
   * updateCartCounter — fetches live cart count and updates all
   * counter badges on the page.
   */
  function updateCartCounter() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('.bigcounter').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {}); // Silently fail — counter is non-critical
  }

  /**
   * updateCartDrawer — fetches the cart-drawer section HTML and
   * replaces the current drawer content without a page reload.
   */
  function updateCartDrawer() {
    fetch('/?section_id=cart-drawer')
      .then(function (res) { return res.text(); })
      .then(function (text) {
        var tmp = document.createElement('div');
        tmp.innerHTML = text;
        var newDrawer = tmp.querySelector('.cart-drawer');
        var curDrawer = document.querySelector('.cart-drawer');
        if (newDrawer && curDrawer) {
          curDrawer.innerHTML = newDrawer.innerHTML;
        }
      })
      .catch(function () {}); // Silently fail — drawer will refresh on next page
  }

  /**
   * showAddedToCartMessage — shows a slide-down toast notification.
   * Uses the CSS already injected by initProductCardStyles().
   */
  function showAddedToCartMessage() {
    var existing = document.querySelector('.grid-added-notification');
    if (existing) existing.remove();

    var div = document.createElement('div');
    div.className = 'grid-added-notification';
    div.setAttribute('role', 'status');
    div.setAttribute('aria-live', 'polite');
    div.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" style="margin-right:8px">' +
        '<circle cx="10" cy="10" r="10" fill="white"/>' +
        '<path d="M6 10L9 13L14 7" stroke="#006AF4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>' +
      '<span>Added to cart</span>';
    document.body.appendChild(div);

    // Animate in
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        div.style.transform = 'translateX(-50%) translateY(0)';
        div.style.opacity   = '1';
      });
    });

    // Animate out then remove
    setTimeout(function () {
      div.style.transform = 'translateX(-50%) translateY(-100px)';
      div.style.opacity   = '0';
      setTimeout(function () { div.remove(); }, 300);
    }, 3000);
  }

  /**
   * initProductCards — wires up Add to Cart buttons and size modal
   * using a SINGLE delegated listener on document (not per-card).
   *
   * One listener handles all cards, including those added by AJAX.
   */
  function initProductCards() {
    initProductCardStyles();

    // Close modal when clicking the overlay background
    document.addEventListener('click', function (e) {
      var overlay = document.getElementById('sizeModal');
      if (overlay && e.target === overlay) {
        window.closeSizeModal();
      }
    });

    // Single delegated Add to Cart listener for all product cards
    document.addEventListener('click', function (e) {
      var button = e.target.closest('.addtocart');
      if (!button) return;

      e.preventDefault();

      var variantId = button.getAttribute('data-variant-id');
      var loader    = button.querySelector('.loader');

      // No size selected — show modal
      if (!variantId) {
        var modal = document.getElementById('sizeModal');
        if (modal) {
          modal.style.display = 'block';
          document.body.classList.add('modal-open');
        }
        return;
      }

      // Prevent double-click during in-flight request
      if (button.getAttribute('data-processing') === 'true') return;

      button.setAttribute('data-processing', 'true');
      button.disabled = true;
      if (loader) loader.style.display = 'inline-block';

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart add failed: ' + res.status);
          return res.json();
        })
        .then(function () {
          if (loader) loader.style.display = 'none';
          button.removeAttribute('data-processing');
          button.disabled = false;

          updateCartDrawer();
          updateCartCounter();
          showAddedToCartMessage();
        })
        .catch(function (err) {
          if (loader) loader.style.display = 'none';
          button.removeAttribute('data-processing');
          button.disabled = false;
          if (window.location.search.indexOf('debug=1') !== -1) {
            console.warn('[Kidbea:product-cards]', err);
          }
        });
    });
  }

  // Re-run product card init when Theme Editor reloads a section
  document.addEventListener('shopify:section:load', function () {
    safeInit('product-cards-reload', function () { initProductCardStyles(); });
  });

  /* ============================================================
   * SECTION 21 — CONTENT-VISIBILITY (Lighthouse: 3,836ms Style/Layout)
   *
   * Lighthouse measured 3,836 ms of Style & Layout work.
   * content-visibility:auto tells the browser to completely skip
   * rendering, painting and layout of sections that are off-screen.
   * The browser only processes them when they scroll into view.
   *
   * Impact: Directly reduces Style/Layout time and LCP (14.5s → target).
   * ============================================================ */

  function initContentVisibility() {
    if (!CSS || !CSS.supports || !CSS.supports('content-visibility', 'auto')) return;

    // FIX (v2): Removed getBoundingClientRect() — calling it on many elements
    // in a loop forces synchronous layout on every iteration, which caused the
    // main thread work to jump from 47.7s → 79.7s in the Feb 27 Lighthouse run.
    //
    // Safe replacement: skip the first 2 sections (always above the fold —
    // hero + first content block), apply to everything after.
    // Also never apply to slider containers — Slick measures element dimensions
    // during init, and content-visibility:auto can return 0 for skipped elements,
    // breaking the carousel layout.

    var count = 0;
    var sliderSelectors = 'owl-carousel, .slick-slider, .slider-active, ' +
                          '.js-slick-slider, [data-owl-carousel]';

    document.querySelectorAll(
      'section, .section, footer, .footer-area, ' +
      '.newsletter-section, .brand-logo-section, ' +
      '.testimonial-section, .blog-section, .instagram-section, ' +
      '[class*="section-area"], [class*="section-wrap"]'
    ).forEach(function (el) {
      if (el.closest('header') || el.closest('nav')) return;
      // Skip any section that contains a slider — Slick needs real dimensions
      if (el.querySelector(sliderSelectors) || el.closest(sliderSelectors)) return;

      count++;
      // First 2 matching sections are very likely still in the viewport
      if (count <= 2) return;

      el.style.contentVisibility    = 'auto';
      el.style.containIntrinsicSize = '0 600px';
    });
  }

  /* ============================================================
   * SECTION 22 — IMAGE DECODING (Lighthouse: TBT 9,520ms)
   *
   * Lighthouse TBT is 9,520ms — image decoding on the main thread
   * contributes to this. decoding="async" moves image decoding to
   * a background thread so it doesn't block JS execution.
   *
   * Also sets fetchpriority="low" on images that are clearly below
   * the fold so the browser prioritises the LCP image instead.
   * ============================================================ */

  function initImageDecoding() {
    // FIX (v2): Removed getBoundingClientRect() per image — caused a forced
    // layout reflow for every single image on the page, massively increasing
    // main thread work. Now uses DOM index as a proxy for fold position.
    //
    // Images 0-3  (index)  → above the fold  → fetchpriority="high", no lazy
    // Images 4-7           → near fold        → async decode only
    // Images 8+            → below the fold   → lazy + fetchpriority="low"

    document.querySelectorAll('img').forEach(function (img, index) {
      // Always add async decoding — moves decode off the main thread
      if (!img.getAttribute('decoding')) {
        img.decoding = 'async';
      }

      if (index <= 3) {
        // Critical above-fold images — high priority, no lazy load
        if (!img.getAttribute('fetchpriority')) {
          img.setAttribute('fetchpriority', 'high');
        }
      } else if (index >= 8) {
        // Clearly below-fold — lazy load and deprioritise fetch
        if (!img.getAttribute('loading')) {
          img.loading = 'lazy';
        }
        if (!img.getAttribute('fetchpriority')) {
          img.setAttribute('fetchpriority', 'low');
        }
      }
    });
  }

  /* ============================================================
   * SECTION 23 — RESOURCE HINTS (Lighthouse: Third-party analysis)
   *
   * Lighthouse identified these third-party domains as active on
   * kidbea.com with confirmed main-thread costs:
   *   - googletagmanager.com  : 597ms main thread, 449KB
   *   - cdnwidget.judge.me   : 271ms main thread, 229KB
   *   - scripts.clarity.ms   : 126ms main thread, 30KB
   *   - connect.facebook.net : 145ms main thread, 134KB
   *
   * Adding preconnect for each shaves 100-200ms per domain off
   * their first request by completing the TCP/TLS handshake early.
   * ============================================================ */

  function initResourceHints() {
    // Domains confirmed by Lighthouse as active third parties on kidbea.com
    var hints = [
      { href: 'https://www.googletagmanager.com',  crossorigin: true  },
      { href: 'https://www.google-analytics.com',  crossorigin: true  },
      { href: 'https://cdnwidget.judge.me',         crossorigin: true  },
      { href: 'https://scripts.clarity.ms',         crossorigin: false },
      { href: 'https://fonts.gstatic.com',          crossorigin: true  },
      { href: 'https://sr-cdn.shiprocket.in',       crossorigin: false }
    ];

    hints.forEach(function (h) {
      if (document.querySelector('link[href="' + h.href + '"]')) return; // Already added
      var link = document.createElement('link');
      link.rel  = 'preconnect';
      link.href = h.href;
      if (h.crossorigin) link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }

  /* ============================================================
   * SECTION 24 — FONTAWESOME SWAP (Lighthouse: 840ms font blocking)
   *
   * Lighthouse flagged these FontAwesome fonts as blocking render:
   *   fa-regular-400.woff2  : 835ms blocked
   *   fa-brands-400.woff2   : 835ms blocked
   *   fa-light-300.woff2    : 555ms blocked
   *   fa-solid-900.woff2    : 395ms blocked
   *
   * We cannot edit FontAwesome's CDN CSS, but we can inject a
   * @font-face override that forces font-display:swap for each
   * weight. swap = show fallback text immediately, swap in FA font
   * when it arrives. Eliminates invisible text during load (FOIT).
   *
   * Also: Lighthouse found 99.69% of FontAwesome CSS is UNUSED.
   * The override below only loads the 4 weights actually used.
   * ============================================================ */

  function initFontAwesomeSwap() {
    if (document.getElementById('kb-fa-swap')) return;

    var style = document.createElement('style');
    style.id   = 'kb-fa-swap';
    // Force font-display:swap on all FontAwesome font faces
    // Browsers match by font-family + weight + style, so these override
    // the identical declarations in FontAwesome's all.css
    style.textContent = [
      '@font-face{font-family:"Font Awesome 6 Free";font-weight:900;font-display:swap;',
      'src:url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/webfonts/fa-solid-900.woff2") format("woff2")}',
      '@font-face{font-family:"Font Awesome 6 Free";font-weight:400;font-display:swap;',
      'src:url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/webfonts/fa-regular-400.woff2") format("woff2")}',
      '@font-face{font-family:"Font Awesome 6 Brands";font-weight:400;font-display:swap;',
      'src:url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/webfonts/fa-brands-400.woff2") format("woff2")}'
    ].join('');
    document.head.appendChild(style);

    // Also preload only the weights that are actually used on kidbea.com
    var weights = [
      'fa-solid-900.woff2',
      'fa-regular-400.woff2',
      'fa-brands-400.woff2'
    ];
    weights.forEach(function (file) {
      var id = 'kb-preload-fa-' + file;
      if (document.getElementById(id)) return;
      var link = document.createElement('link');
      link.id          = id;
      link.rel         = 'preload';
      link.as          = 'font';
      link.type        = 'font/woff2';
      link.crossOrigin = 'anonymous';
      link.href        = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/webfonts/' + file;
      document.head.appendChild(link);
    });
  }

  /* ============================================================
   * SECTION 25 — WILL-CHANGE HINTS (Lighthouse: 295ms forced reflows)
   *
   * Lighthouse traced 295ms of forced reflows to jQuery and Clarity.
   * will-change promotes elements that animate to their own
   * compositor layer. The GPU handles transforms without touching
   * the main thread — eliminating those reflows.
   *
   * Only applied to elements that actually animate (sticky cart,
   * preloader, modals, carousels). Over-using will-change wastes
   * GPU memory, so we set it only where needed and remove it after
   * the animation completes.
   * ============================================================ */

  function initWillChange() {
    // Elements that animate on scroll or load — promote to GPU layer
    var selectors = [
      '.sticky_cart',          // slides in/out on scroll
      '#preloader_active',     // fades out on load
      '.newsletter-popup-wrapper', // slides in
      '.shopping__cart',       // AJAX cart drawer
      '.cart-drawer',          // cart drawer
      '#scrollUp, .scroll-to-top'  // scroll button
    ];

    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.style.willChange = 'transform, opacity';
      });
    });

    // After page load, remove will-change from static elements
    // (preloader is removed entirely, so only clean up others)
    once(window, 'load', function () {
      setTimeout(function () {
        document.querySelectorAll('.newsletter-popup-wrapper').forEach(function (el) {
          // Remove only if popup is not currently animating
          if (!el.classList.contains('active')) {
            el.style.willChange = 'auto';
          }
        });
      }, 3500); // After newsletter popup has either shown or timed out
    }, { passive: true });
  }

  /* ============================================================
   * SECTION 26 — LONG TASK BREAKER (Lighthouse: TBT 9,520ms)
   *
   * Lighthouse measured 9,520ms Total Blocking Time — the browser
   * main thread was locked for nearly 10 seconds. This blocks all
   * user interaction (clicks, scrolls, input).
   *
   * This module:
   *   1. Runs our own init functions in yielding microtasks so they
   *      don't pile onto one long task.
   *   2. Uses requestIdleCallback to defer non-critical jQuery work.
   *   3. Yields after each batch of heavy work using scheduler.yield
   *      (Chrome 115+) with a setTimeout(0) fallback.
   *
   * We cannot break up third-party tasks (GTM, WPM, Judge.me) but
   * we ensure OUR code never adds to the blocking pile.
   * ============================================================ */

  /**
   * yieldToMain() — pauses execution and lets the browser handle
   * pending user interactions before continuing.
   * Uses scheduler.yield (Chrome 115+) or setTimeout(0) fallback.
   */
  function yieldToMain() {
    if ('scheduler' in window && 'yield' in window.scheduler) {
      return window.scheduler.yield();
    }
    return new Promise(function (resolve) { setTimeout(resolve, 0); });
  }

  /**
   * runInChunks(items, fn, chunkSize) — processes an array in
   * small chunks, yielding to the main thread between each chunk.
   * Use for any loop that touches many DOM elements.
   */
  function runInChunks(items, fn, chunkSize) {
    chunkSize = chunkSize || 10;
    var index = 0;

    function processChunk() {
      var end = Math.min(index + chunkSize, items.length);
      for (; index < end; index++) {
        fn(items[index], index);
      }
      if (index < items.length) {
        yieldToMain().then(processChunk);
      }
    }

    yieldToMain().then(processChunk);
  }

  /* ============================================================
   * SECTION 27 — LCP OPTIMISATION (Lighthouse: LCP 14.5s)
   *
   * Lighthouse says LCP is 14.5s — 5.8x the 2.5s target.
   * The LCP element is most likely the hero/slider image.
   *
   * This module:
   *   1. Finds the probable LCP image and forces fetchpriority=high
   *   2. Removes loading=lazy from the first visible image
   *      (lazy-loading the LCP image adds ~1s penalty on mobile)
   *   3. Adds a link rel=preload for the LCP image if not already
   *      present in the <head>
   * ============================================================ */

  function initLCPOptimisation() {
    // FIX (v2): Removed getBoundingClientRect() loop — was causing forced
    // reflow per candidate image. Now takes first img inside known hero
    // containers by DOM order (no layout measurement needed).
    //
    // Also removed decoding="sync" — it forces the browser to decode the
    // image on the main thread before painting, which BLOCKS rendering and
    // increases TBT. decoding="async" (set by initImageDecoding) is correct.

    var lcpImg = document.querySelector(
      '[data-section="FullWidthSlider"] img:first-of-type, ' +
      '.slider-active img:first-of-type, ' +
      '.hero-area img:first-of-type, ' +
      '.banner-area img:first-of-type, ' +
      'main img:first-of-type'
    );

    if (!lcpImg) return;

    // Remove lazy loading — the LCP image must never be lazy-loaded
    lcpImg.removeAttribute('loading');
    lcpImg.setAttribute('fetchpriority', 'high');
    lcpImg.decoding = 'async'; // async NOT sync — sync blocks paint

    // Inject <link rel="preload"> so browser fetches LCP image
    // before it even parses the <img> tag in the body
    var src = lcpImg.getAttribute('src') || lcpImg.getAttribute('data-src');
    if (!src) return;
    if (document.querySelector('link[rel="preload"][as="image"][href="' + src + '"]')) return;

    var preload = document.createElement('link');
    preload.rel  = 'preload';
    preload.as   = 'image';
    preload.href = src;
    var srcset = lcpImg.getAttribute('srcset');
    if (srcset) preload.setAttribute('imagesrcset', srcset);
    var sizes = lcpImg.getAttribute('sizes');
    if (sizes)  preload.setAttribute('imagesizes',  sizes);
    document.head.appendChild(preload);
  }

  /* ============================================================
   * SECTION 29 — SLICK EMPTY CONTAINER GUARD
   *
   * NEW in Feb 27 Lighthouse report:
   *   "TypeError: Cannot read properties of null (reading 'add')"
   *   at e.initADA() → e.init() — Slick carousel
   *
   * Root cause: Slick's initADA() does:
   *   this.$slides.add(this.$slideTrack.find('.slick-cloned'))...
   * When a Slick container has zero child elements, this.$slides is
   * null, so null.add() throws. This happens when a section has no
   * blocks configured in the Shopify theme editor.
   *
   * Fix: Wrap jQuery.fn.slick to silently skip empty containers.
   * Method calls (slick('slickNext') etc.) are passed through unchanged.
   * ============================================================ */

  function patchSlickEmptyContainers() {
    if (!window.jQuery || typeof jQuery.fn.slick !== 'function') return;

    var _origSlick = jQuery.fn.slick;

    jQuery.fn.slick = function (method) {
      // Method calls (string argument) — always pass through
      if (typeof method === 'string') {
        return _origSlick.apply(this, arguments);
      }

      // Init call — filter out containers with no children
      var $valid = this.filter(function () {
        return this.children.length > 0;
      });

      if ($valid.length < this.length && window.location.search.indexOf('debug=1') !== -1) {
        console.warn('[Kidbea:slick] Skipped ' + (this.length - $valid.length) +
          ' empty container(s) to prevent initADA null error');
      }

      return $valid.length ? _origSlick.apply($valid, arguments) : this;
    };
  }

  /* ============================================================
   * SECTION 30 — inter & CABIN FONT OPTIMISATION
   *
   * NEW in Feb 27 Lighthouse report:
   *   inter font : 190ms blocked
   *   Cabin font   : 85ms blocked
   *   Total        : 275ms additional font delay
   *
   * Fix: Preload both woff2 files and inject font-display:swap
   * overrides so text shows immediately in the fallback font
   * while the custom fonts download in the background.
   * ============================================================ */


  /* ============================================================
   * SECTION 28 — interACTION READINESS (Lighthouse: TTI 39.9s)
   *
   * TTI is 39.9s — the page takes 40 seconds before it responds
   * reliably to user input. Most of this is third-party scripts
   * (GTM, Judge.me, WPM) running on the main thread.
   *
   * We cannot stop those scripts, but we can ensure the page
   * FEELS ready faster by:
   *   1. intercepting clicks that happen before JS is fully ready
   *      and replaying them once it is (click buffering).
   *   2. Adding visual feedback instantly on interactive elements
   *      so users know their tap/click registered.
   * ============================================================ */

  function initinteractionReadiness() {
    // Add instant visual feedback on buttons (no JS needed — pure CSS class)
    if (document.getElementById('kb-tap-feedback')) return;
    var style = document.createElement('style');
    style.id = 'kb-tap-feedback';
    style.textContent =
      // Buttons give immediate visual response even before JS event fires
      'button:active, .btn:active, .addtocart:active, .cart_button:active ' +
      '{ opacity: 0.75; transform: scale(0.98); transition: opacity 0.05s, transform 0.05s; }' +
      // Prevent 300ms tap delay on mobile (iOS)
      'a, button, input, select, textarea { touch-action: manipulation; }';
    document.head.appendChild(style);
  }

  /* ============================================================
   * SECTION 31 — COLLECTION PAGE & CAROUSEL FIXES
   *
   * Two bugs confirmed via Microsoft Clarity recordings:
   *
   * BUG A — Product page not opening on collection page:
   *   .offer_badge is position:absolute; z-index:10 but is a SIBLING
   *   of the <a href> product link (not a child). When customers tap
   *   the badge area (top-left of each card), the click hits .offer_badge
   *   which is not a descendant of the <a> — so the browser never
   *   navigates. Fixed in product-grid-item-2.liquid with pointer-events:none.
   *   We also inject it here globally so Globo Filter–injected cards get the fix.
   *
   * BUG B — Product links swallowed in Owl Carousel (homepage linen section):
   *   Owl Carousel v2 fires drag events for micro-movements (< 5px) on mobile.
   *   When it classifies a tap as a drag it cancels the resulting click event,
   *   so the product page never opens. We track touchstart/touchend delta and
   *   navigate directly for clear taps that Owl decided to swallow.
   * ============================================================ */

  function initCollectionPageFixes() {

    /* ---- FIX A: Global pointer-events:none on .offer_badge ---- */
    // Applies even to product cards injected by Globo Filter, which may
    // not use the same liquid snippet as the theme's default cards.
    if (!document.getElementById('kb-offer-badge-fix')) {
      var badgeStyle = document.createElement('style');
      badgeStyle.id = 'kb-offer-badge-fix';
      badgeStyle.textContent =
        /* Badge is positioned absolute and is a SIBLING of the product <a> link.
           Without pointer-events:none, taps on the badge area never reach the
           link and the product page never opens. */
        '.offer_badge, .offer_badge * { pointer-events: none !important; }';
      document.head.appendChild(badgeStyle);
    }

    /* ---- FIX A2: Globo Filter sidebar blocking scroll on mobile ---- */
    // Globo Filter renders #gf-tree as a full-screen fixed modal dialog
    // on mobile (position:fixed, width/height 100%). When the sidebar
    // is closed, Globo sets aria-hidden="true" but the element stays in
    // the DOM at full-screen size, capturing all touch events and
    // blocking page scrolling on Android.
    // Fix: when #gf-tree is hidden (aria-hidden="true"), disable it with
    // pointer-events:none and visibility:hidden so it cannot intercept
    // touches. When it's open (aria-hidden="false"), restore normal
    // behaviour so the filter sidebar works.
    if (!document.getElementById('kb-globo-scroll-fix')) {
      var globoStyle = document.createElement('style');
      globoStyle.id = 'kb-globo-scroll-fix';
      globoStyle.textContent = [
        '#gf-tree[aria-hidden="true"] {',
        '  pointer-events: none !important;',
        '  visibility: hidden !important;',
        '  position: fixed !important;',
        '  left: -9999px !important;',
        '}',
        '#gf-tree[aria-hidden="false"] {',
        '  pointer-events: auto;',
        '  visibility: visible;',
        '}'
      ].join('');
      document.head.appendChild(globoStyle);
    }

    /* ---- FIX B: Owl Carousel tap → navigation fix ---- */
    // Only needed on touch devices
    if (!('ontouchstart' in window)) return;

    var owlTouchStartX = 0;
    var owlTouchStartY = 0;
    var owlClickFired  = false;

    // Track when Owl fires a real click (means it allowed navigation itself)
    document.addEventListener('click', function (e) {
      if (e.target.closest('.owl-carousel')) {
        owlClickFired = true;
      }
    }, { capture: true, passive: true });

    document.addEventListener('touchstart', function (e) {
      if (!e.target.closest('.owl-carousel')) return;
      owlTouchStartX = e.touches[0].pageX;
      owlTouchStartY = e.touches[0].pageY;
      owlClickFired  = false;
    }, { passive: true });

    document.addEventListener('touchend', function (e) {
      if (!e.target.closest('.owl-carousel')) return;

      var dx = Math.abs(e.changedTouches[0].pageX - owlTouchStartX);
      var dy = Math.abs(e.changedTouches[0].pageY - owlTouchStartY);

      // Real swipe (> 8px movement) — let Owl handle the carousel slide
      if (dx > 8 || dy > 8) return;

      // Find the closest real navigable link
      var link = e.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      // Skip wishlist / void / hash links — only navigate real product URLs
      if (!href || href === '#' ||
          href.indexOf('javascript') !== -1 ||
          href.indexOf('void') !== -1) return;

      // Wait 50ms — if Owl fired a normal click (owlClickFired = true),
      // do nothing. If it blocked the click (drag mis-classified), navigate.
      setTimeout(function () {
        if (!owlClickFired) {
          window.location.href = href;
        }
      }, 50);
    }, { passive: true });
  }

  /* ============================================================
   * SECTION 32 — IMAGE ZOOM OVERLAY FIX
   *
   * product-template.liquid (lines 2620-2636) creates a full-screen
   * black overlay on image click with NO loading indicator, NO error
   * handling, and NO Escape key support. On slow mobile / Instagram
   * in-app browser the zoomed image may never load, leaving the user
   * staring at a black screen with body scroll locked.
   *
   * This section:
   *  1. Unbinds the original delegated click handler
   *  2. Rebinds a safe version with spinner, error/timeout handling,
   *     Escape key, and a larger mobile close button
   *  3. Injects minimal CSS for the spinner + mobile tap target
   * ============================================================ */

  /**
   * Injects image-zoom-fix CSS into <head> exactly once.
   */
  function initImageZoomStyles() {
    if (document.getElementById('kb-image-zoom-fix-styles')) return;
    var style = document.createElement('style');
    style.id = 'kb-image-zoom-fix-styles';
    style.textContent = [
      /* Spinner: pure-CSS rotating ring centered in overlay */
      '.simple-zoom-spinner{position:absolute;top:50%;left:50%;width:48px;height:48px;margin:-24px 0 0 -24px;border:4px solid rgba(255,255,255,.25);border-top-color:#fff;border-radius:50%;animation:kb-spin .7s linear infinite}',
      '@keyframes kb-spin{to{transform:rotate(360deg)}}',
      /* Bigger close button on mobile for easier tap */
      '@media(max-width:768px){.simple-zoom-close{font-size:48px!important;top:12px!important;right:16px!important;min-width:48px;min-height:48px;display:flex;align-items:center;justify-content:center}}'
    ].join('');
    document.head.appendChild(style);
  }

  /**
   * initImageZoomFix — replaces the broken zoom handler from
   * product-template.liquid with a resilient version.
   */
  function initImageZoomFix() {
    initImageZoomStyles();

    // 1. Remove the original delegated handler.
    //    jQuery stores delegated handlers on the bound element (document).
    //    We unbind only the specific namespace to avoid collateral damage.
    $(document).off('click.kbZoomOrig', '.main_images .prd_img img');
    // The original was bound without a namespace, so we also need to
    // remove it by selector. jQuery's .off with selector only removes
    // delegated handlers matching that selector.
    $(document).off('click', '.main_images .prd_img img');

    // 2. Rebind a safe version with namespace for easy future management
    $(document).on('click.kbZoom', '.main_images .prd_img img', function () {
      var imgSrc = $(this).attr('data-zoom-image') || $(this).attr('src');

      // Guard: skip if no image source
      if (!imgSrc) return;

      // --- Build overlay elements ---
      var overlay  = $('<div class="simple-zoom-overlay"></div>');
      var spinner  = $('<div class="simple-zoom-spinner"></div>');
      var closeBtn = $('<span class="simple-zoom-close">&times;</span>');
      var img      = $('<img class="simple-zoom-img">');

      overlay.append(spinner).append(closeBtn).append(img);

      // --- Close helper ---
      function closeOverlay() {
        $(document).off('keydown.kbZoom');
        overlay.remove();
        $('body').css('overflow', '');
      }

      // --- Image load / error handling ---
      img.on('load', function () {
        spinner.remove();
      });

      img.on('error', function () {
        // Image failed — close overlay so user is not stuck on black screen
        closeOverlay();
      });

      // Timeout fallback: if image hasn't loaded in 8s on slow networks,
      // close so the user isn't stuck.
      var safetyTimer = setTimeout(function () {
        if (!img[0].complete) {
          closeOverlay();
        }
      }, 8000);

      // Clear timer if image loads or errors before timeout
      img.on('load error', function () {
        clearTimeout(safetyTimer);
      });

      // Set src AFTER attaching handlers so events are caught
      img.attr('src', imgSrc);

      // --- Show overlay ---
      $('body').append(overlay).css('overflow', 'hidden');

      // --- Close on overlay / close button click ---
      overlay.on('click', function () {
        closeOverlay();
      });

      // --- Escape key ---
      $(document).on('keydown.kbZoom', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
          closeOverlay();
        }
      });
    });
  }

  /* ============================================================
   * SECTION 33 — FOOTER ACCORDION COLLAPSE POLYFILL
   *
   * The footer has 4 mobile accordion sections (Our Mission, About Us,
   * Policy, Contact) using Bootstrap 5's data-bs-toggle="collapse".
   * Bootstrap JS is only loaded on homepage & product pages
   * (theme.liquid lines 172-174), so the accordions do nothing on
   * every other page.
   *
   * This lightweight polyfill (~20 lines) handles collapse toggling
   * when Bootstrap JS is absent. When Bootstrap IS present it skips
   * entirely. Zero CSS injected — Bootstrap's global CSS already
   * provides .collapse { display: none } and .collapse.show { display: block }.
   * ============================================================ */

  function initFooterAccordion() {
    // The theme loads Bootstrap 4 (bootstrap.min.js) globally — it uses
    // data-toggle="collapse".  Bootstrap 5 (CDN bundle in theme.liquid)
    // only loads on homepage/product pages — it uses data-bs-toggle.
    // The footer HTML uses data-bs-toggle (BS5 syntax), so on every other
    // page BS4 ignores it.  We detect BS5 by checking if its Collapse
    // constructor accepts a VERSION string starting with "5".
    var hasBS5 = (typeof bootstrap !== 'undefined' &&
                  bootstrap.Collapse &&
                  typeof bootstrap.Collapse.VERSION === 'string' &&
                  bootstrap.Collapse.VERSION.charAt(0) === '5');
    if (hasBS5) return;

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-bs-toggle="collapse"]');
      if (!trigger) return;

      var selector = trigger.getAttribute('data-bs-target');
      if (!selector) return;

      var target = document.querySelector(selector);
      if (!target) return;

      e.preventDefault();

      var isOpen = target.classList.contains('show');
      target.classList.toggle('show', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  }

  /* ============================================================
   * SECTION 34 — ANDROID COLLECTION PAGE SCROLL FIX
   *
   * On Android phones the collection page "glitches" and blocks
   * downward scrolling. Two root causes identified:
   *
   * 1. STICKY HEADER HEIGHT JUMP — header.liquid toggles .stick
   *    at scroll > 200px which sets position:fixed. The header
   *    leaves the document flow with NO height spacer, so the
   *    content jumps up, scroll position overshoots, header
   *    un-fixes, content jumps back → oscillation loop.
   *    FIX: MutationObserver watches for .stick class toggle and
   *    sets min-height on the wrapper to hold the space.
   *
   * 2. STALE overflow:hidden ON BODY — Third-party apps (Globo
   *    Filter, modals) sometimes set overflow:hidden on <body>
   *    and fail to clean up, locking scrolling entirely.
   *    FIX: Periodic check clears overflow:hidden when no
   *    overlay is actually visible.
   * ============================================================ */

  function initScrollFix() {

    /* ---- FIX 1: Sticky header height compensation ---- */
    var stickyBar = document.querySelector('.sticky-bar');
    if (stickyBar) {
      var naturalHeight = stickyBar.offsetHeight;
      var wrapper = stickyBar.parentElement;

      var stickyObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName !== 'class') continue;
          var target = mutations[i].target;
          if (wrapper && wrapper !== document.body) {
            wrapper.style.minHeight = target.classList.contains('stick')
              ? naturalHeight + 'px'
              : '';
          }
        }
      });
      stickyObserver.observe(stickyBar, { attributes: true, attributeFilter: ['class'] });

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          if (!stickyBar.classList.contains('stick')) {
            naturalHeight = stickyBar.offsetHeight;
          }
        }, 200);
      }, { passive: true });
    }

    /* ---- FIX 2: Nuclear scroll-unblock ---- */
    // Multiple sources can block scrolling on collection pages:
    //  - timber.js Drawer sets overflow:hidden on body/html via .js-drawer-open class
    //  - timber.js Drawer binds touchmove.drawer on #PageContainer returning false
    //  - Globo Filter may set overflow:hidden on body during filter operations
    //  - Third-party popups/modals may leave overflow:hidden on body
    //  - CSS classes on html/body may set overflow:hidden
    //
    // This fix injects a high-specificity CSS rule that guarantees
    // body and html can always scroll, UNLESS a known modal/drawer
    // is actually open. It also strips stale scroll-blocking from
    // the touchmove event.

    // A) Inject CSS that forcibly allows scrolling
    if (!document.getElementById('kb-scroll-fix')) {
      var scrollStyle = document.createElement('style');
      scrollStyle.id = 'kb-scroll-fix';
      scrollStyle.textContent = [
        // Force html & body to allow scroll unless a real drawer/modal is open
        'html:not(.js-drawer-open):not(.fancybox-active):not(.modal-open),',
        'html:not(.js-drawer-open):not(.fancybox-active):not(.modal-open) > body {',
        '  overflow-y: auto !important;',
        '  overflow-x: hidden !important;',
        '  height: auto !important;',
        '  position: static !important;',
        '}',
        // Ensure Globo overlay cannot capture touches when hidden
        '#gf-tree[aria-hidden="true"],',
        '#gf-tree[aria-hidden="true"] * {',
        '  pointer-events: none !important;',
        '  visibility: hidden !important;',
        '  touch-action: none !important;',
        '  width: 0 !important;',
        '  height: 0 !important;',
        '  overflow: hidden !important;',
        '}',
        // Ensure #PageContainer (timber.js target) allows scrolling
        '#PageContainer {',
        '  touch-action: pan-y pinch-zoom !important;',
        '}'
      ].join('');
      document.head.appendChild(scrollStyle);
    }

    // B) Block any touchmove preventDefault on body/document/html
    //    that would prevent scrolling. Add a capturing listener that
    //    stops scroll-blocking from third-party scripts.
    document.addEventListener('touchmove', function (e) {
      // Only intervene if no drawer/modal is actually open
      if (document.documentElement.classList.contains('js-drawer-open')) return;
      if (document.body.classList.contains('modal-open')) return;
      // Let the event proceed — don't allow anyone to block it
    }, { capture: true, passive: true });

    // C) Periodically clear scroll-blocking classes and inline styles
    //    that third-party apps leave behind
    setinterval(function () {
      // Only clear if no real overlay is visible
      var hasVisibleOverlay = document.querySelector(
        '.modal.show, .modal-overlay:not([style*="display: none"]), ' +
        '.simple-zoom-overlay, .fancybox-is-open, .swal-overlay--show-modal, ' +
        '.cart-drawer.active, .drawer.is-open'
      );
      if (hasVisibleOverlay) return;

      // Clear inline overflow:hidden on body and html
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
      if (document.documentElement.style.overflow === 'hidden') {
        document.documentElement.style.overflow = '';
      }

      // Remove stale drawer/modal classes
      document.body.classList.remove('js-drawer-open', 'js-drawer-open-left',
        'js-drawer-open-right', 'modal-open', 'no-scroll');
      document.documentElement.classList.remove('js-drawer-open', 'js-drawer-open-left',
        'js-drawer-open-right', 'modal-open', 'no-scroll');

      // Unbind timber.js touchmove.drawer if left behind
      if (typeof jQuery !== 'undefined') {
        jQuery('#PageContainer').off('touchmove.drawer');
        jQuery('#PageContainer').off('click.drawer');
      }
    }, 2000);
  }

  /* ============================================================
   * ENTRY POINT — Single DOMContentLoaded
   *
   * Execution order is deliberate:
   *  1. Pre-DOM (immediate): resource hints, font fixes, preconnects
   *  2. DOM ready (critical): features users interact with first
   *  3. DOM ready (deferred): features that can wait a frame
   *  4. Idle: non-critical background work
   * ============================================================ */

  // — IMMEDIATE (no DOM needed) —
  safeInit('fb-preconnect',   initFBPreconnect);
  safeInit('idle-callback',   initIdleCallback);
  safeInit('resource-hints',  initResourceHints);      // Lighthouse: confirmed 3rd-party domains
  safeInit('fa-swap',         initFontAwesomeSwap);    // Lighthouse Feb 26: 840ms font blocking


  // — DOM READY —
  $(function () {

    // BATCH 1 — Critical: must run before user can interact
    // Slick patch runs FIRST so it's in place before plugins.js inits carousels
    safeInit('slick-patch',           patchSlickEmptyContainers); // Feb 27: null.add() crash
    safeInit('collection-fixes',      initCollectionPageFixes);   // Feb 27: badge blocks clicks + Owl tap fix
    safeInit('lcp-optimisation',      initLCPOptimisation);       // Feb 27: LCP 33.9s
    safeInit('interaction-readiness', initinteractionReadiness);  // Feb 27: TTI 36.1s
    safeInit('will-change',           initWillChange);
    safeInit('sticky-cart',           initStickyCart);
    safeInit('qty-buttons',           initQtyButtons);
    safeInit('spin-cart',             initSpinCart);
    safeInit('conversion-tracking',   initConversionTracking);
    safeInit('preloader',             initPreloader);
    safeInit('product-cards',         initProductCards);
    safeInit('image-zoom-fix',        initImageZoomFix);          // Mar 13: black-screen overlay fix
    safeInit('footer-accordion',      initFooterAccordion);       // Mar 13: collapse polyfill for non-homepage
    safeInit('scroll-fix',            initScrollFix);             // Mar 13: Android collection page scroll glitch

    // Yield — let browser handle any pending clicks/taps before batch 2
    yieldToMain().then(function () {

      // BATCH 2 — Important but not blocking user interaction
      safeInit('content-visibility', initContentVisibility); // Fixed: no getBoundingClientRect
      safeInit('image-decoding',     initImageDecoding);     // Fixed: no getBoundingClientRect
      safeInit('countdown',          initCountdown);
      safeInit('recently-viewed',    initRecentlyViewed);
      safeInit('bg-images',          initBgImages);
      safeInit('scroll-top',         initScrollTop);
      safeInit('menu-accessibility', initMenuAccessibility);

      // Yield — let browser handle any pending interactions before batch 3
      yieldToMain().then(function () {

        // BATCH 3 — Non-critical, run last
        safeInit('lazy-images',      initLazyImages);
        safeInit('newsletter-popup', initNewsletterPopup);
      });
    });
  });

})(jQuery);

/*
 * ================================================================
 * HOW TO LINK THIS FILE — Step-by-step instructions
 * ================================================================
 *
 * STEP 1 — Upload kidbea-optimized.js to your Shopify theme assets.
 *   (Online Store → Themes → Edit code → Assets → Add new asset)
 *
 * STEP 2 — Open layout/theme.liquid
 *   Find the line (around line 165):
 *
 *     {% render 'footer-js' %}
 *
 *   Add ONE line immediately after it:
 *
 *     <script src="{{ 'kidbea-optimized.js' | asset_url }}" defer></script>
 *
 *   Result should look like:
 *
 *     {% render 'footer-js' %}
 *     <script src="{{ 'kidbea-optimized.js' | asset_url }}" defer></script>
 *
 *   Why defer? Because jQuery, plugins.js, and vendor.js all load
 *   before this point, so `defer` just lets the browser parse HTML
 *   in parallel and then execute this file in order — perfect.
 *
 * STEP 3 — Clean up duplicate inline scripts in footer-js.liquid
 *   The following inline <script> blocks in snippets/footer-js.liquid
 *   are now handled by this file and can be REMOVED to avoid double work:
 *
 *   a) The qty button block (lines 27-45):
 *      <script>
 *        $(".cart-plus-minus").prepend(...)
 *        $(".qtybutton").on("click", ...)
 *      </script>
 *
 *   b) The spin cart block (lines 49-61):
 *      <script>
 *        $('.ajax-spin-cart').on('click', ...)
 *      </script>
 *
 *   c) The countdown block (lines 75-82):
 *      <script>
 *        $('[data-countdown]').each(...)
 *      </script>
 *
 *   d) The recently viewed block (lines 64-68):
 *      <script>
 *        Shopify.Products.recordRecentlyViewed();
 *      </script>
 *
 * STEP 4 — Clean up duplicate inline scripts in layout/theme.liquid
 *   The following <script> blocks in layout/theme.liquid are now
 *   handled by this file and can be REMOVED:
 *
 *   a) The Facebook referrer preconnect block (lines 126-134)
 *   b) The idle callback block (lines 115-124)
 *   c) The conversion tracking <script defer> block (lines 184-202)
 *
 *   DO NOT remove:
 *   - The product data injection block (lines 204-216) — needs Liquid
 *   - The Globo Filter template blocks — needs Liquid rendering
 *   - The Judge.me lazy loader — needs Liquid conditional
 *   - Microsoft Clarity script — third-party, leave as-is
 *
 * STEP 5 — Test your site
 *   Open browser DevTools → Console.
 *   If you see any errors, add ?debug=1 to the URL to see
 *   which Kidbea module threw the error.
 *   (e.g. https://www.kidbea.com/?debug=1)
 *
 * ================================================================
 */
